import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useEmail } from '../contexts/EmailContext';
import EmailCampaignBuilder from '../components/Email/EmailCampaignBuilder';
import EmailTemplateLibrary from '../components/Email/EmailTemplateLibrary';
import EmailAnalyticsDashboard from '../components/Email/EmailAnalyticsDashboard';
import SafeIcon from '../common/SafeIcon';
import { FiCalendar, FiEdit, FiEye, FiFilter, FiMail, FiMousePointer, FiPause, FiPlay, FiPlus, FiSend, FiTrash2, FiUsers } from 'react-icons/fi';


const Email = () => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [showCampaignBuilder, setShowCampaignBuilder] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const { campaigns, sendCampaign, deleteCampaign, getCampaignStats, loading } = useEmail();

  const stats = getCampaignStats();
  
  const filteredCampaigns = campaigns.filter(campaign => 
    filterStatus === 'all' || campaign.status === filterStatus
  );

  const tabs = [
    { id: 'campaigns', label: 'Campaigns', icon: FiMail },
    { id: 'templates', label: 'Templates', icon: FiEdit },
    { id: 'analytics', label: 'Analytics', icon: FiUsers }
  ];

  const statCards = [
    {
      title: 'Total Campaigns',
      value: stats.total,
      change: `${stats.active} active`,
      icon: FiMail,
      color: 'blue'
    },
    {
      title: 'Emails Sent',
      value: stats.sent.toLocaleString(),
      change: `${stats.delivered} delivered`,
      icon: FiSend,
      color: 'green'
    },
    {
      title: 'Open Rate',
      value: stats.sent > 0 ? `${Math.round((stats.opened / stats.sent) * 100)}%` : '0%',
      change: `${stats.opened} opens`,
      icon: FiEye,
      color: 'purple'
    },
    {
      title: 'Click Rate',
      value: stats.sent > 0 ? `${Math.round((stats.clicked / stats.sent) * 100)}%` : '0%',
      change: `${stats.clicked} clicks`,
      icon: FiMousePointer,
      color: 'orange'
    }
  ];

  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600'
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.draft;
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setShowCampaignBuilder(true);
  };

  const handleCloseCampaignBuilder = () => {
    setShowCampaignBuilder(false);
    setSelectedCampaign(null);
  };

  const handleSendCampaign = async (campaignId) => {
    await sendCampaign(campaignId);
  };

  const renderCampaigns = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.change}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${colorClasses[stat.color]} flex items-center justify-center`}>
                <SafeIcon icon={stat.icon} className="text-white text-xl" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Email Campaigns</h3>
              <p className="text-sm text-gray-600">Manage your email marketing campaigns</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiFilter} className="text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Campaigns</option>
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="sent">Sent</option>
                  <option value="completed">Completed</option>
                  <option value="paused">Paused</option>
                </select>
              </div>
              <button
                onClick={() => setShowCampaignBuilder(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} className="text-sm" />
                Create Campaign
              </button>
            </div>
          </div>
        </div>

        {filteredCampaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCampaigns.map((campaign, index) => (
                  <motion.tr
                    key={campaign.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                        <div className="text-sm text-gray-500">{campaign.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-900 capitalize">{campaign.type}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {campaign.recipients?.length || 0}
                    </td>
                    <td className="px-6 py-4">
                      {campaign.stats.sent > 0 ? (
                        <div className="text-sm">
                          <div className="text-gray-900">
                            Sent: {campaign.stats.sent}
                          </div>
                          <div className="text-gray-500">
                            Open: {Math.round((campaign.stats.opened / campaign.stats.sent) * 100)}% | 
                            Click: {Math.round((campaign.stats.clicked / campaign.stats.sent) * 100)}%
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No data</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(campaign.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        {campaign.status === 'draft' && (
                          <button
                            onClick={() => handleSendCampaign(campaign.id)}
                            disabled={loading}
                            className="p-1 text-gray-400 hover:text-green-600 transition-colors disabled:opacity-50"
                            title="Send campaign"
                          >
                            <SafeIcon icon={FiSend} className="text-sm" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEditCampaign(campaign)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit campaign"
                        >
                          <SafeIcon icon={FiEdit} className="text-sm" />
                        </button>
                        <button
                          onClick={() => deleteCampaign(campaign.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete campaign"
                        >
                          <SafeIcon icon={FiTrash2} className="text-sm" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <SafeIcon icon={FiMail} className="text-4xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
            <p className="text-gray-600 mb-4">
              {filterStatus !== 'all' 
                ? 'No campaigns match the selected filter.'
                : 'Create your first email campaign to start nurturing prospects.'
              }
            </p>
            <button
              onClick={() => setShowCampaignBuilder(true)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Create Campaign
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Marketing</h1>
          <p className="text-gray-600">
            Create and manage email campaigns to nurture your recruitment prospects
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SafeIcon icon={tab.icon} className="text-sm" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'campaigns' && renderCampaigns()}
        {activeTab === 'templates' && <EmailTemplateLibrary />}
        {activeTab === 'analytics' && <EmailAnalyticsDashboard />}
      </motion.div>

      {/* Campaign Builder Modal */}
      {showCampaignBuilder && (
        <EmailCampaignBuilder
          campaign={selectedCampaign}
          onClose={handleCloseCampaignBuilder}
        />
      )}
    </div>
  );
};

export default Email;